import { createContext, useContext, useEffect, useId } from 'react';
import type { ApiClient } from '../../api';
import { StatusBadge, rechenstand, type ExportZiel, type Vorbehalt } from '../../muster';
import type { MsrRechenstand } from './typen';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * KOPF DER MSR-SEITEN (Paket TM-8a, TX-STANDARD S1.2, S1.9)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Die MSR-Unterseiten sind Reiter EINER Seite (`MsrRahmen`, ein `<h1>`). Ihre
 * Exportleiste steht deshalb im Kopf des Rahmens: Jede Unterseite - und jede
 * Detailkarte darin (ASP, Einstellwerte einer Anlage) - meldet ihre Ziele über
 * {@link useMsrExport}; der Rahmen zeigt sie nach Rang geordnet (Seite 0,
 * ASP 1, Anlage 2). Ohne Rahmen (Einzeltest) geht die Meldung ins Leere.
 *
 * Der Rechenstand hat die Pflichtform von S1.2 und die Marke aus
 * `fachstatus.rechenstand`; „veraltet“ heißt: Der Lauf ist älter als der
 * Modellstand, die jüngste Aufnahme der Eingänge (Regel des Gewerkstands).
 */

/** Meldet die Ziele einer Stelle (`null` = abmelden). */
export type MsrExportMelden = (stelle: string, rang: number, ziele: ExportZiel[] | null) => void;

export const MsrExportKontext = createContext<MsrExportMelden>(() => {});

/** Rang der Meldung: die Seite, eine ASP-Karte, eine Anlagenkarte. */
export const RANG_SEITE = 0;
export const RANG_ASP = 1;
export const RANG_ANLAGE = 2;

/**
 * Meldet die Exportziele dieser Stelle an den Kopf, solange sie zu sehen ist.
 * `bilden` läuft neu, wenn sich `abhaengig` ändert (z. B. der Filter).
 */
export function useMsrExport(rang: number, bilden: () => ExportZiel[], abhaengig: readonly unknown[]) {
  const melden = useContext(MsrExportKontext);
  const stelle = useId();
  useEffect(() => {
    melden(stelle, rang, bilden());
    return () => melden(stelle, rang, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [melden, stelle, rang, ...abhaengig]);
}

/** Die Ziele aller Stellen in Rangfolge, innerhalb eines Rangs in Meldefolge. */
export function zieleOrdnen(stellen: readonly { rang: number; ziele: ExportZiel[] }[]): ExportZiel[] {
  return stellen
    .map((s, i) => ({ s, i }))
    .sort((a, b) => a.s.rang - b.s.rang || a.i - b.i)
    .flatMap((x) => x.s.ziele);
}

/**
 * Die Excel-Ziele der MSR-Listen - EINE Stelle für Beschriftung und Route. Alle laufen über den Excel-Kern
 * (TM-2a/TM-2b); die Vorlagenlisten nach VDI 3814 Blatt 4.1 und 4.3 bleiben bei ihren Knöpfen (F-7).
 */
export const msrZiele = {
  datenpunktliste: (api: ApiClient, p: string, f?: { asp?: string; gewerk?: string }): ExportZiel => ({
    art: 'excel', beschriftung: 'Excel Datenpunktliste',
    laden: () => api.msrDatenpunktlisteExcel(p, 'MSR_Datenpunktliste.xlsx', f),
    hinweis: f?.asp || f?.gewerk ? 'mit dem Filter ASP/Gewerk' : undefined,
  }),
  funktionsliste: (api: ApiClient, p: string): ExportZiel => ({
    art: 'excel', beschriftung: 'Excel Funktionsliste', laden: () => api.msrFunktionslisteExcel(p, 'MSR_Funktionsliste.xlsx'),
  }),
  aspListe: (api: ApiClient, p: string): ExportZiel => ({
    art: 'excel', beschriftung: 'Excel ASP-Liste', laden: () => api.msrAspListeExcel(p, 'MSR_ASP-Liste.xlsx'),
  }),
  feldgeraete: (api: ApiClient, p: string): ExportZiel => ({
    art: 'excel', beschriftung: 'Excel Feldgeräteliste', laden: () => api.msrFeldgeraeteExcel(p, 'MSR_Feldgeraeteliste.xlsx'),
  }),
  raumautomation: (api: ApiClient, p: string): ExportZiel => ({
    art: 'excel', beschriftung: 'Excel Raumautomationsliste',
    laden: () => api.msrRaumautomationExcel(p, 'MSR_Raumautomationsliste.xlsx'),
  }),
  kabel: (api: ApiClient, p: string, asp?: { id: string; kennung: string }): ExportZiel => ({
    art: 'excel', beschriftung: asp ? `Excel Kabel und Klemmen ${asp.kennung}` : 'Excel Kabel und Klemmen',
    laden: () => api.msrKabelExcel(p, asp?.id),
  }),
  klemmenbelegung: (api: ApiClient, p: string, asp: { id: string; kennung: string }): ExportZiel => ({
    art: 'excel', beschriftung: `Excel Klemmenbelegung ${asp.kennung}`,
    laden: () => api.msrKlemmenbelegungExcel(p, asp.id, `MSR_Klemmenbelegung_${asp.kennung}.xlsx`),
  }),
  einstellwerteAsp: (api: ApiClient, p: string, asp: { id: string; kennung: string }): ExportZiel => ({
    art: 'excel', beschriftung: `Excel Einstellwerte ${asp.kennung}`,
    laden: () => api.msrEinstellwerteExcel(p, `MSR_Einstellwerte_${asp.kennung}.xlsx`, { asp: asp.id }),
  }),
  einstellwerteAnlage: (api: ApiClient, p: string, anlage: { id: string; kennung: string }): ExportZiel => ({
    art: 'excel', beschriftung: `Excel Einstellwerte ${anlage.kennung}`,
    laden: () => api.msrEinstellwerteExcel(p, `MSR_Einstellwerte_${anlage.kennung}.xlsx`, { anlage: anlage.id }),
  }),
  schrankbilanz: (api: ApiClient, p: string): ExportZiel => ({
    art: 'excel', beschriftung: 'Excel Schrankbilanz', laden: () => api.msrSchrankbilanzExcel(p),
  }),
  befunde: (api: ApiClient, p: string): ExportZiel => ({
    art: 'excel', beschriftung: 'Excel Befunde', laden: () => api.msrBefundeExcel(p),
  }),
  effizienz: (api: ApiClient, p: string): ExportZiel => ({
    art: 'excel', beschriftung: 'Excel Effizienzklasse', laden: () => api.msrEffizienzExcel(p),
  }),
  mbus: (api: ApiClient, p: string): ExportZiel => ({
    art: 'excel', beschriftung: 'Excel M-Bus', laden: () => api.msrMbusExcel(p),
  }),
  isp: (api: ApiClient, p: string): ExportZiel => ({
    art: 'excel', beschriftung: 'Excel ISP-Listen', laden: () => api.msrIspExcel(p),
  }),
};

// ═══ Rechenstand (S1.2) ═════════════════════════════════════════════════════

const ZEIT = new Intl.DateTimeFormat('de-DE', {
  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Berlin',
});
const TAG = new Intl.DateTimeFormat('de-DE', {
  day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Berlin',
});

/** „08.10.2026, 18:20“ (Ortszeit Deutschland); leer bei einer Fehlstelle. */
export function zeitpunkt(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : ZEIT.format(d);
}

/** „12.10.2026“; leer bei einer Fehlstelle. */
export function tag(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : TAG.format(d);
}

/** „Lauf ‹Kurz-ID› · gerechnet ‹…› · Modellstand ‹…›“; ohne Lauf „noch nicht gerechnet“ (S1.2). */
export function rechenstandText(stand: MsrRechenstand | null | undefined): string {
  if (!stand?.lauf) return 'noch nicht gerechnet';
  const teile = [`Lauf ${stand.lauf.kurzId}`, `gerechnet ${zeitpunkt(stand.lauf.gerechnetAm)}`];
  if (stand.modellstand) teile.push(`Modellstand ${tag(stand.modellstand)}`);
  return teile.join(' · ');
}

/** Der Rechenstand als Text mit Marke (aktuell / veraltet / nicht gerechnet). */
export function MsrRechenstandAnzeige({ stand }: { stand: MsrRechenstand | null | undefined }) {
  if (stand === undefined) return null;
  const s = rechenstand(stand?.lauf?.gerechnetAm ?? null, stand?.modellstand ?? null);
  return (
    <span data-rechenstand>
      {rechenstandText(stand)}{' '}
      <StatusBadge {...s} klein />
    </span>
  );
}

/** Der Vorbehalt „veraltet“ mit den Eingängen, die nach dem Lauf aufgenommen wurden; sonst keiner. */
export function veraltetVorbehalt(stand: MsrRechenstand | null | undefined): Vorbehalt | null {
  if (!stand?.lauf || !stand.veraltet) return null;
  const neu = stand.eingaenge.filter((e) => e.nachDemLauf)
    .map((e) => `${e.bezeichnung} ${tag(e.letzteAufnahme)}`);
  return {
    art: 'veraltet',
    text: `Der Ableitungslauf vom ${zeitpunkt(stand.lauf.gerechnetAm)} ist älter als der Modellstand `
      + `${tag(stand.modellstand)}`
      + (neu.length > 0 ? ` - danach aufgenommen: ${neu.join(', ')}` : '')
      + '. Die MSR-Listen, Seiten und Dokumente zeigen den Stand des Laufs.',
  };
}
